import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainHeader from "../../Components/MainHeader";
import { Button } from "react-bootstrap";
import CartShowcase from "../../Components/CartShowcase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../firebase";
import AlertDialog from "../../Components/AlertDialog";
import { 
  FaCar, 
  FaUser, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaIdCard, 
  FaCalendarAlt,
  FaTachometerAlt,
  FaExclamationTriangle,
  FaCreditCard,
  FaShieldAlt,
  FaCheckCircle
} from 'react-icons/fa';

const Checkout = () => {
  const location = useLocation();
  const {
    cartItems = [],
    totalPrice = 0,
    discount = 0,
    total = 0,
  } = location.state || {};
  const navigate = useNavigate();
  
  // Simple state object
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    address2: "",
    country: "",
    state: "",
    zip: "",
    carNumber: "",
    model: "",
    make: "",
    regDate: "",
    motExpDate: "",
    phoneNumber: "",
    currentKm: "",
    vinNumber: "",
    vehicleAccident: "",
  });

  // Pre-populate fields from cart items if available
  useEffect(() => {
    if (cartItems.length > 0) {
      const firstItem = cartItems[0];
      setFormData(prev => ({
        ...prev,
        name: firstItem.name || "",
        email: firstItem.email || "",
        address: firstItem.address || "",
        address2: firstItem.address2 || "",
        country: firstItem.country || "",
        state: firstItem.state || "",
        zip: firstItem.zip || firstItem.postcode || "",
        carNumber: firstItem.carNumber || "",
        model: firstItem.model || firstItem.modelName || firstItem.Model || "",
        make: firstItem.make || "",
        regDate: firstItem.regDate || firstItem.firstRegistrationDate || "",
        motExpDate: firstItem.motExpDate || firstItem.motExpiryDate || "",
        phoneNumber: firstItem.phoneNumber || firstItem.phone || "",
        currentKm: firstItem.currentKm || "",
        vinNumber: firstItem.vinNumber || "",
        vehicleAccident: firstItem.vehicleAccident || "",
      }));
    }
  }, [cartItems]);

  // Simple input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateFields = () => {
    const requiredFields = [
      "name", "email", "address", "country", "state", "zip",
      "carNumber", "make", "model", "regDate", "motExpDate", "phoneNumber"
    ];
    
    const missingFields = requiredFields.filter(field => !formData[field]?.trim());
    
    if (missingFields.length > 0) {
      const formattedFields = missingFields.map(field => {
        return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      }).join(', ');
      
      AlertDialog(
        "Missing Information",
        `Please fill in the following required fields: ${formattedFields}`,
        "warning", 3000, true, false, "OK", "", () => {}, null
      );
      return false;
    }
    return true;
  };

  const saveOrder = async () => {
    try {
      const orderData = {
        details: formData,
        items: cartItems,
        price: { total, totalPrice, discount },
        createdAt: new Date().toISOString(),
      };
      await addDoc(collection(db, "Orders"), orderData);
      toast.success("Order saved successfully.");
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error("There was an error saving your order.");
      throw error;
    }
  };

  const handleNavigate = async () => {
    if (!validateFields()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    
    try {
      AlertDialog("Processing", "Your order is being prepared...", "info", 1500, false, false, "", "", () => {}, null);
      
      setTimeout(async () => {
        try {
          await saveOrder();
          AlertDialog("Order Prepared", "Taking you to payment...", "success", 800, false, false, "", "", () => {}, null);
          
          setTimeout(() => {
            navigate("/Payment-Process", {
              state: {
                price: [total, totalPrice, discount],
                details: formData,
                items: cartItems,
              },
            });
          }, 1000);
          
        } catch (error) {
          AlertDialog("Error", "There was a problem processing your order. Please try again.", "error", 2000, true, false, "OK", "", () => {}, null);
        }
      }, 1000);
      
    } catch (error) {
      console.error("Navigation error:", error);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" />
      <MainHeader />
      
      <div className="checkout-container">
        {/* Modern Header */}
        <div className="checkout-header">
          <div className="header-content">
            <div className="header-icon">
              <FaCar />
            </div>
            <div className="header-text">
              <h1>Secure Checkout</h1>
              <p>Complete your automotive service purchase with confidence</p>
            </div>
          </div>
          <div className="security-badge">
            <FaShieldAlt />
            <span>SSL Secured</span>
          </div>
        </div>

        <div className="checkout-layout">
          {/* Order Summary */}
          <div className="order-summary">
            <CartShowcase items={cartItems} price={[total, totalPrice, discount]} />
            
            <div className="trust-section">
              <h3>Why Choose Us?</h3>
              <div className="trust-items">
                <div className="trust-item">
                  <FaCheckCircle />
                  <span>Secure Payment</span>
                </div>
                <div className="trust-item">
                  <FaCheckCircle />
                  <span>Expert Service</span>
                </div>
                <div className="trust-item">
                  <FaCheckCircle />
                  <span>Money Back Guarantee</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Form */}
          <div className="form-container">
            {/* Personal Information */}
            <div className="form-section">
              <div className="section-title">
                <FaUser />
                <h2>Personal Information</h2>
              </div>
              
              <div className="form-grid">
                <div className="input-group">
                  <label>
                    <FaUser className="input-icon" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>
                    <FaEnvelope className="input-icon" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>
                    <FaPhone className="input-icon" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="+61 4XXX XXXXXX"
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <div className="section-title">
                <FaMapMarkerAlt />
                <h2>Billing Address</h2>
              </div>
              
              <div className="form-grid">
                <div className="input-group full-width">
                  <label>
                    <FaMapMarkerAlt className="input-icon" />
                    Street Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main Street"
                    className="form-input"
                  />
                </div>

                <div className="input-group full-width">
                  <label>Address Line 2</label>
                  <input
                    type="text"
                    name="address2"
                    value={formData.address2}
                    onChange={handleInputChange}
                    placeholder="Apartment, suite, etc. (optional)"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="United Kingdom"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>State/County *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="Greater London"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>Postal Code *</label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    placeholder="SW1A 1AA"
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="form-section">
              <div className="section-title">
                <FaCar />
                <h2>Vehicle Information</h2>
              </div>
              
              <div className="form-grid">
                <div className="input-group">
                  <label>
                    <FaIdCard className="input-icon" />
                    Registration Number *
                  </label>
                  <input
                    type="text"
                    name="carNumber"
                    value={formData.carNumber}
                    onChange={handleInputChange}
                    placeholder="AB12 CDE"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>Make *</label>
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    placeholder="e.g., BMW, Audi, Mercedes"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>Model *</label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="e.g., 3 Series, A4, C-Class"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>
                    <FaCalendarAlt className="input-icon" />
                    Build Date *
                  </label>
                  <input
                    type="date"
                    name="regDate"
                    value={formData.regDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>
                    <FaCalendarAlt className="input-icon" />
                    Last Service Date *
                  </label>
                  <input
                    type="date"
                    name="motExpDate"
                    value={formData.motExpDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>
                    <FaTachometerAlt className="input-icon" />
                    Current Mileage (KM)
                  </label>
                  <input
                    type="number"
                    name="currentKm"
                    value={formData.currentKm}
                    onChange={handleInputChange}
                    placeholder="e.g., 50,000"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>VIN Number</label>
                  <input
                    type="text"
                    name="vinNumber"
                    value={formData.vinNumber}
                    onChange={handleInputChange}
                    placeholder="17-character Vehicle ID"
                    className="form-input"
                  />
                </div>

                <div className="input-group">
                  <label>
                    <FaExclamationTriangle className="input-icon" />
                    Vehicle Accident History
                  </label>
                  <select
                    name="vehicleAccident"
                    value={formData.vehicleAccident}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Please select...</option>
                    <option value="No">No accident history</option>
                    <option value="Yes">Previous accident/write-off</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Service Summary */}
            {cartItems.length > 0 && (
              <div className="form-section">
                <div className="section-title">
                  <FaCheckCircle />
                  <h2>Service Summary</h2>
                </div>
                
                <div className="service-items">
                  {cartItems.map((item, index) => (
                    <div key={item.id || index} className="service-card">
                      <div className="service-header">
                        <FaCar />
                        <span>Service {index + 1}</span>
                      </div>
                      <div className="service-details">
                        {item.activePlan && (
                          <p><strong>Plan:</strong> {item.activePlan.plan_name} - {item.activePlan.plan_duration} - ${item.activePlan.price}</p>
                        )}
                        {item.bookingDate && (
                          <p><strong>Booking Date:</strong> {item.bookingDate}</p>
                        )}
                        {item.serviceType && (
                          <p><strong>Service Type:</strong> {item.serviceType}</p>
                        )}
                        {item.productDescription && (
                          <p><strong>Description:</strong> {item.productDescription}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Checkout Button */}
            <div className="checkout-actions">
              <Button 
                className="checkout-btn"
                onClick={handleNavigate}
                size="lg"
              >
                <FaCreditCard />
                Proceed to Secure Payment
              </Button>
              <p className="security-note">
                <FaShieldAlt /> Your information is encrypted and secure
              </p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        * {
          box-sizing: border-box;
        }

        .checkout-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 1rem;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .checkout-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem;
          border-radius: 20px;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .header-icon {
          font-size: 3.5rem;
          color: #a8e6cf;
        }

        .header-text h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 0 0 0.5rem 0;
          background: linear-gradient(45deg, #fff, #a8e6cf);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .header-text p {
          margin: 0;
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .security-badge {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: rgba(255, 255, 255, 0.15);
          padding: 1rem 1.5rem;
          border-radius: 30px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          font-weight: 600;
        }

        .checkout-layout {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 2rem;
          align-items: start;
        }

        .order-summary {
          position: sticky;
          top: 2rem;
        }

        .trust-section {
          background: white;
          border-radius: 15px;
          padding: 1.5rem;
          margin-top: 1rem;
          box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
        }

        .trust-section h3 {
          margin: 0 0 1rem 0;
          color: #333;
          font-size: 1.2rem;
        }

        .trust-items {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .trust-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #27ae60;
          font-weight: 500;
        }

        .trust-item svg {
          color: #27ae60;
          font-size: 1.1rem;
        }

        .form-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .form-section {
          background: white;
          border-radius: 20px;
          padding: 2rem;
          box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
          border: 1px solid #e8ecef;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f1f3f5;
        }

        .section-title svg {
          font-size: 1.5rem;
          color: #667eea;
        }

        .section-title h2 {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 600;
          color: #2c3e50;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        .input-group {
          display: flex;
          flex-direction: column;
        }

        .input-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #34495e;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
        }

        .input-icon {
          color: #667eea;
          font-size: 1rem;
        }

        .form-input {
          padding: 1rem 1.25rem;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          font-size: 1rem;
          font-family: inherit;
          background: white;
          transition: all 0.3s ease;
          width: 100%;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
          background: #f8f9ff;
        }

        .form-input::placeholder {
          color: #adb5bd;
        }

        .service-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .service-card {
          background: #f8f9fa;
          border-radius: 15px;
          padding: 1.5rem;
          border-left: 4px solid #667eea;
        }

        .service-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
          font-weight: 600;
          color: #667eea;
          font-size: 1.1rem;
        }

        .service-details p {
          margin: 0.5rem 0;
          color: #6c757d;
        }

        .service-details strong {
          color: #495057;
        }

        .checkout-actions {
          text-align: center;
          background: white;
          border-radius: 20px;
          padding: 2rem;
          box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
        }

        .checkout-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 1.25rem 3rem;
          font-size: 1.2rem;
          font-weight: 600;
          border-radius: 15px;
          color: white;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.3s ease;
          box-shadow: 0 5px 20px rgba(102, 126, 234, 0.3);
          margin-bottom: 1rem;
        }

        .checkout-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(102, 126, 234, 0.4);
          background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
        }

        .security-note {
          color: #6c757d;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin: 0;
        }

        @media (max-width: 768px) {
          .checkout-layout {
            grid-template-columns: 1fr;
          }
          
          .order-summary {
            order: 2;
            position: static;
          }
          
          .checkout-header {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }
          
          .form-grid {
            grid-template-columns: 1fr;
          }
          
          .form-section {
            padding: 1.5rem;
          }
          
          .checkout-container {
            padding: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default Checkout;